// ─── Relances au jour choisi par le client (2026-09-15, migration 157) ──────
//
// When a client names a day (« je confirme lundi »), the assistant schedules
// ONE message for that day, quoting their words, and every other follow-up
// waits for it. The owner: « I want to SEE it before it fires, not after. »
//
// So this card is the schedule itself: who, what they wrote, when it will go
// out, whether it actually can, and the exact text.
//
// ⚠️ THE TEXT SHOWN IS NOT WRITTEN HERE. `preview` comes from the worker's
// `composePromiseFollowUp` — the same function the sweep drafts with — and the
// time from `promiseFiresAt`, which the send window is defined from. A second
// copy of the Darija in this file would drift, and the owner would approve a
// message customers never receive. Guard: ClientPromisesCard.test.tsx.
//
// ⚠️ THE SWITCH LIVES WITH THE OTHER LADDER SWITCHES, not here — TestNumbersCard
// « Ce que l’assistant envoie tout seul », column `ladder_client_promise_enabled`.
// One box per famille de message, in one place.
import { useQuery } from '@tanstack/react-query';
import { whatsappApi, type ClientPromiseBlock } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { SettingGroup } from './_ui';

/** ⚠️ ONE MOROCCO CLOCK (2026-09-29). This used a fixed UTC+1 because the
 *  worker's promise day did. The worker now reads the tz database
 *  (worker/src/lib/nightHold.ts) — Ramadan is UTC+0 there — so the card shows
 *  the instant the worker will send on Morocco's clock, as the tz database
 *  knows it: the time the owner will read on his own phone. */
const CASABLANCA = 'Africa/Casablanca';

/** « lun. 7 sept. à 10:00 », in Casablanca time. Empty for a missing date. */
export function firesLabel(iso: string | null | undefined): string {
  const ms = Date.parse(String(iso ?? ''));
  if (!Number.isFinite(ms)) return '';
  const at = new Date(ms);
  const day = at.toLocaleDateString('fr-FR', { timeZone: CASABLANCA, weekday: 'short', day: 'numeric', month: 'short' });
  const time = at.toLocaleTimeString('fr-FR', { timeZone: CASABLANCA, hour: '2-digit', minute: '2-digit' });
  return `${day} à ${time}`;
}

/** Why it will not go out, in the owner's words. One entry per worker reason —
 *  the test fails if the worker grows one this map does not know. */
export const BLOCKED_LABEL: Record<ClientPromiseBlock, string> = {
  settings_unreadable: 'Réglages illisibles — rien ne part tant que ce n’est pas réglé.',
  switch_off: 'Case coupée — ce message ne partira pas.',
  send_off: 'Envoi automatique coupé — ce message ne partira pas.',
  not_allowlisted: 'Numéro hors de la liste des destinataires — ce message ne partira pas.',
  no_room_in_day: 'Promis trop tard dans la journée — ce message ne partira pas.',
};

export function ClientPromisesCard() {
  const { t } = useT();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['client-promises'],
    queryFn: () => whatsappApi.clientPromises(),
    refetchInterval: 120_000,
  });

  const items = data?.items ?? [];
  const enabled = data?.enabled === true;
  const ready = items.filter((i) => i.will_send).length;

  return (
    <SettingGroup
      title={t('Relances au jour choisi par le client')}
      summary={
        isLoading
          ? t('Chargement…')
          : !enabled
            ? t('Coupé — rien ne part le jour annoncé')
            : items.length === 0
              ? t('Aucune relance programmée')
              : `${items.length} ${t('relance(s) programmée(s)')} · ${ready} ${t('partira(ont)')}`
      }
      tone="default"
      defaultOpen={items.length > 0}
      loading={isLoading}
    >
      <p className="text-[11.5px] text-gray-500 mt-3 leading-snug max-w-2xl">
        {t(
          'Quand un client annonce un jour (« je confirme lundi »), il reçoit un seul message ce jour-là, '
          + 'à partir de 10 h, qui reprend ses propres mots. En attendant, les autres relances ne lui écrivent pas. '
          + 'Le message ne part pas si le client écrit avant, ou si une autre réponse est déjà en file pour lui.',
        )}
      </p>

      {!isLoading && !enabled && (
        <p className="text-[11.5px] text-amber-700 mt-2 leading-snug">
          {t('La case « Relancer le client le jour qu’il a lui-même choisi » est coupée (Envoi automatique → Ce que l’assistant envoie tout seul). Les autres relances continuent normalement.')}
        </p>
      )}

      {isError && (
        <p className="text-[11.5px] text-red-600 mt-3" role="alert">
          {t('Impossible de charger les relances programmées.')}
        </p>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-[12px] text-gray-400 mt-3">
          {t('Aucun client n’a annoncé de jour pour le moment.')}
        </p>
      )}

      <div className="mt-3 space-y-2">
        {items.map((it) => (
          <div key={it.chat_id} className="rounded-lg border border-gray-100 bg-white px-3.5 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-semibold text-gray-800 truncate">
                {it.customer_name || t('Sans nom')}
                {it.order?.name ? <span className="font-normal text-gray-500"> · {it.order.name}</span> : null}
              </span>
              <span className="text-[11px] text-gray-400 tabular-nums shrink-0">
                {it.phone ? `+${it.phone.replace(/\D/g, '')}` : ''}
              </span>
            </div>

            <p className="text-[11.5px] text-gray-600 mt-1 leading-snug">
              {t('A écrit')} « {it.words} »
            </p>

            {/* ⚠️ Never colour alone: the line always says in words whether it goes out. */}
            <p className={`text-[11.5px] mt-1 leading-snug ${it.will_send ? 'text-green-700' : 'text-amber-700'}`}>
              {it.fires_at
                ? `${it.will_send ? t('Partira le') : t('Prévu le')} ${firesLabel(it.fires_at)}`
                : `${t('Jour annoncé :')} ${it.day}`}
              {it.blocked ? ` — ${t(BLOCKED_LABEL[it.blocked])}` : ''}
            </p>

            <p className="text-[11px] font-semibold text-gray-500 mt-2">{t('Message qui partira')}</p>
            <p className="mt-1 whitespace-pre-wrap text-[12px] text-gray-700 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 leading-snug">
              {it.preview}
            </p>
          </div>
        ))}
      </div>
    </SettingGroup>
  );
}
